export interface ParsedInstagramProfile {
    name: string;
    description?: string;
}

/** Signals the segment is a tagline/role, not a personal name. */
const DESCRIPTION_HINT_RE =
    /יוצר|מפתח|שף|בעל|מתכון|קולינר|food|chef|recipe|creator|content|cook|baker|kitchen|author/i;

function looksLikeDescription(segment: string): boolean {
    return DESCRIPTION_HINT_RE.test(segment);
}

/**
 * Splits Instagram `full_name` blobs such as
 * "יעל קצב | יוצרת תוכן קולינרי • מפתחת מתכונים" into display name + description.
 */
export function parseInstagramFullName(fullName: string, fallbackName = ''): ParsedInstagramProfile {
    const trimmed = fullName.trim();
    if (!trimmed) return { name: fallbackName };

    const pipeParts = trimmed.split(/\s*[|｜]\s*/).map((part) => part.trim()).filter(Boolean);
    if (pipeParts.length >= 2) {
        return {
            name: pipeParts[0],
            description: pipeParts.slice(1).join(' | '),
        };
    }

    const bulletParts = trimmed.split(/\s*[•·]\s*/).map((part) => part.trim()).filter(Boolean);
    if (bulletParts.length >= 2) {
        const firstIsDescription = looksLikeDescription(bulletParts[0]);
        const lastIsDescription = looksLikeDescription(bulletParts[bulletParts.length - 1]);

        if (!firstIsDescription && lastIsDescription) {
            return {
                name: bulletParts[0],
                description: bulletParts.slice(1).join(' • '),
            };
        }
        if (firstIsDescription && !lastIsDescription) {
            return {
                name: bulletParts[bulletParts.length - 1],
                description: bulletParts.slice(0, -1).join(' • '),
            };
        }

        const shortest = bulletParts.reduce((a, b) => (a.length <= b.length ? a : b));
        if (shortest.length <= 30 && bulletParts[0] === shortest) {
            return {
                name: bulletParts[0],
                description: bulletParts.slice(1).join(' • '),
            };
        }
    }

    const dashMatch = trimmed.match(/^(.{2,40}?)\s[-–—]\s+(.+)$/);
    if (dashMatch) {
        return { name: dashMatch[1].trim(), description: dashMatch[2].trim() };
    }

    return { name: trimmed };
}

export function profileFromInstagramOwner(
    fullName: string | undefined,
    username: string,
): { name: string; description?: string } {
    const normalizedUsername = username.trim();
    if (!fullName?.trim()) {
        return { name: normalizedUsername };
    }
    const parsed = parseInstagramFullName(fullName, normalizedUsername);
    return {
        name: parsed.name || normalizedUsername,
        description: parsed.description,
    };
}

export function chefNameLooksLikeRawInstagramFullName(chefName: string, rawFullName: string): boolean {
    const trimmedChef = chefName.trim();
    const trimmedRaw = rawFullName.trim();
    if (!trimmedChef || !trimmedRaw) return false;
    return trimmedChef === trimmedRaw || trimmedChef.includes('|') || trimmedChef.includes('•');
}
